import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "../stores/auth";
import { brand } from "../brand";
import { restoreSavedPosition } from "./scroll";

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const routes: RouteRecordRaw[] = [
  { path:'/menu',name:'menu',component:()=>import('../pages/MenuPage.vue'),meta:{title:'Моё меню'} },
  { path:'/products/nutrition',name:'nutrition-products',component:()=>import('../pages/NutritionProductsPage.vue'),meta:{title:'Продукты по КБЖУ'} },
  {
    path: "/",
    name: "home",
    component: () => import("../pages/HomePage.vue"),
    meta: { title: "Каталог" },
  },
  {
    path: "/recipes",
    name: "recipes",
    component: () => import("../pages/RecipesPage.vue"),
    meta: { title: "Рецепты" },
  },
  {
    path: "/recipes/:recipeId",
    name: "recipe",
    component: () => import("../pages/RecipePage.vue"),
    meta: { title: "Рецепт" },
  },
  {
    path: "/c/:categoryId",
    name: "category",
    component: () => import("../pages/CategoryPage.vue"),
    meta: { title: "Категория" },
  },
  {
    path: "/p/:productId",
    name: "product",
    component: () => import("../pages/ProductPage.vue"),
    meta: { title: "Товар" },
  },
  {
    // Корзина доступна ГОСТЮ без логина (набирает свободно, видит счётчик/итог).
    // Вход требуется только на оформлении (/checkout остаётся requiresAuth).
    path: "/cart",
    name: "cart",
    component: () => import("../pages/CartPage.vue"),
    meta: { title: "Корзина" },
  },
  {
    path: "/checkout",
    name: "checkout",
    component: () => import("../pages/CheckoutPage.vue"),
    meta: { title: "Оформление", requiresAuth: true },
  },
  {
    path: "/orders",
    name: "orders",
    component: () => import("../pages/OrdersPage.vue"),
    meta: { title: "Мои заказы", requiresAuth: true },
  },
  {
    path: "/orders/:orderId",
    name: "order",
    component: () => import("../pages/OrderDetailPage.vue"),
    meta: { title: "Заказ", requiresAuth: true },
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("../pages/ProfilePage.vue"),
    meta: { title: "Профиль", requiresAuth: true },
  },
  {
    path: "/profile/constraints",
    name: "profile-constraints",
    component: () => import("../pages/ProfileConstraintsPage.vue"),
    meta: { title: "Ограничения в питании", requiresAuth: true },
  },
  {
    // §2.1 Mini App «Моя лояльность» — запуск из бота по launch-токену (?token=…),
    // без storefront-логина. Данные из CDP-движка /v1/miniapp/loyalty.
    path: "/my-loyalty",
    name: "my-loyalty",
    component: () => import("../pages/MyLoyaltyPage.vue"),
    // Портал лояльности: своё лоялти-меню (Главная/Акции/Карта/Миссии/Ещё), без
    // магазинной обвязки. Открывается по launch-токену (?token=…).
    meta: { title: "Моя лояльность", layout: "portal" },
  },
  // ── Экраны Портала лояльности (макет) — все по launch-токену, layout=portal ──
  { path: "/offers", name: "portal-offers", component: () => import("../pages/portal/OffersPage.vue"), meta: { title: "Акции", layout: "portal" } },
  { path: "/card", name: "portal-card", component: () => import("../pages/portal/CardPage.vue"), meta: { title: "Карта", layout: "portal" } },
  { path: "/transfer", name: "portal-transfer", component: () => import("../pages/portal/TransferPage.vue"), meta: { title: "Перевод баллов", layout: "portal" } },
  { path: "/favorites", name: "portal-favorites", component: () => import("../pages/portal/FavoritesPage.vue"), meta: { title: "Любимые категории", layout: "portal" } },
  { path: "/promo", name: "portal-promo", component: () => import("../pages/portal/PromoCodePage.vue"), meta: { title: "Промокод", layout: "portal" } },
  { path: "/order-history", name: "portal-orders", component: () => import("../pages/portal/OrderHistoryPage.vue"), meta: { title: "История заказов", layout: "portal" } },
  { path: "/order-history/:id", name: "portal-order-detail", component: () => import("../pages/portal/OrderDetailPage.vue"), meta: { title: "Заказ", layout: "portal" } },
  { path: "/gift-choice/:id", name: "portal-gift-choice", component: () => import("../pages/portal/GiftChoicePage.vue"), meta: { title: "Выбрать подарок", layout: "portal" } },
  { path: "/referral", name: "portal-referral", component: () => import("../pages/portal/ReferralPage.vue"), meta: { title: "Пригласить друга", layout: "portal" } },
  { path: "/missions-portal", name: "portal-missions", component: () => import("../pages/portal/MissionsPortalPage.vue"), meta: { title: "Миссии и игры", layout: "portal" } },
  { path: "/portal-more", name: "portal-more", component: () => import("../pages/portal/PortalMorePage.vue"), meta: { title: "Ещё", layout: "portal" } },
  {
    // P0-4 Mini App «Семейный кошелёк» — по launch-токену (?token=…), без логина.
    path: "/shared-wallet",
    name: "shared-wallet",
    component: () => import("../pages/SharedWalletPage.vue"),
    meta: { title: "Семейный кошелёк", layout: "portal" },
  },
  {
    // P0-4 приём приглашения по ссылке /wallet-invite/<code>?token=… — standalone (bare).
    path: "/wallet-invite/:code",
    name: "wallet-invite",
    component: () => import("../pages/WalletInvitePage.vue"),
    meta: { title: "Приглашение в кошелёк", layout: "bare" },
  },
  {
    path: "/loyalty",
    name: "loyalty",
    component: () => import("../pages/LoyaltyPage.vue"),
    meta: { title: "Лояльность", requiresAuth: true },
  },
  {
    // Публичное описание программы (как chaihona.ru/loyalty) — доступно гостю,
    // чтобы понять механику и что попробовать в демо.
    path: "/loyalty/about",
    name: "loyalty-about",
    component: () => import("../pages/LoyaltyAboutPage.vue"),
    meta: { title: "О программе лояльности" },
  },
  {
    path: "/missions",
    name: "missions",
    component: () => import("../pages/MissionsPage.vue"),
    meta: { title: "Мои миссии", requiresAuth: true },
  },
  {
    path: "/auth",
    name: "auth",
    component: () => import("../pages/AuthPage.vue"),
    meta: { title: "Вход", layout: "bare" },
  },
  {
    path: "/auth/verify",
    name: "auth-verify",
    component: () => import("../pages/AuthVerifyPage.vue"),
    meta: { title: "Подтверждение входа", layout: "bare" },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../pages/NotFoundPage.vue"),
    meta: { title: "Страница не найдена" },
  },
];

export const router = createRouter({
  // §5.11 Ф4 — base роутера рантайм (window.__BASE_PATH__), чтобы витрину можно
  // было развернуть под путём (напр. /kafe/) на общем домене без пересборки.
  history: createWebHistory(
    (typeof window !== "undefined" && (window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__) ||
      import.meta.env.BASE_URL,
  ),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return restoreSavedPosition(savedPosition);
    return { top: 0, left: 0 };
  },
});

router.beforeEach(async (to) => {
  // §5.11 Ф4 — гость пришёл по реф-ссылке (?ref=CODE) → запомним код до регистрации.
  const ref = to.query.ref;
  if (typeof ref === "string" && ref.trim()) {
    try { localStorage.setItem("vk_ref", ref.trim()); } catch { /* private mode */ }
  }
  const auth = useAuthStore();
  // Restore session lazily — needed for direct navigations.
  if (auth.me === null) {
    await auth.fetchMe().catch(() => undefined);
  }
  if (to.meta.requiresAuth && !auth.isAuthed) {
    return {
      name: "auth",
      query: { next: to.fullPath },
    };
  }
  if (typeof to.meta.title === "string") {
    document.title = `${to.meta.title} · ${brand.tabName}`;
  }
  return true;
});
